import { scopedStorageKey } from "@/lib/user-scope";

export type PageCacheRecord<T> = {
  updatedAt: string;
  data: T;
};

const PAGE_CACHE_PREFIX = "genrl.page-cache.v1";
const DEFAULT_PAGE_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function readPageCache<T>(
  key: string,
  maxAgeMs = DEFAULT_PAGE_CACHE_MAX_AGE_MS,
): PageCacheRecord<T> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(scopedStorageKey(`${PAGE_CACHE_PREFIX}:${key}`));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PageCacheRecord<T>>;
    const updatedAt = String(parsed.updatedAt || "").trim();
    if (!updatedAt || parsed.data === undefined) return null;
    const age = Date.now() - Date.parse(updatedAt);
    if (!Number.isFinite(age) || age > maxAgeMs) return null;
    return {
      updatedAt,
      data: parsed.data as T,
    };
  } catch {
    return null;
  }
}

export function writePageCache<T>(key: string, data: T): PageCacheRecord<T> | null {
  if (typeof window === "undefined") return null;
  const record: PageCacheRecord<T> = {
    updatedAt: new Date().toISOString(),
    data,
  };
  try {
    window.localStorage.setItem(
      scopedStorageKey(`${PAGE_CACHE_PREFIX}:${key}`),
      JSON.stringify(record),
    );
    return record;
  } catch {
    return null;
  }
}
